import type { ManifestWidget } from "./manifest";

/**
 * Per-(tab, section) human-readable labels for the within-tab section
 * headers introduced by MER-162. The section *key* (e.g. "1.2", "1.3",
 * "L2a.3") is the v8.3 accordion structure from `Docs/`. It is an
 * internal authoring convenience, not a client surface (MER-178).
 *
 * Authored labels live here. When a section has no entry, the renderer
 * falls back to omitting the section header entirely so the dashboard
 * never leaks raw IDs like "1.2" / "1.3.1" / "L2a.3" onto the client UI.
 *
 * MER-199 through MER-210: per-tab application of MER-189's
 * uniform-header pattern. Most rows in those tickets mix more than
 * one `widgetSection()` key (e.g. Operations FL tile row spans both
 * "2.1" and "L2a.1"). SECTION_MERGES lets a per-tab map collapse
 * multiple raw keys into one display group key so they render as a
 * single labeled section.
 *
 * Adding labels for a new tab:
 *   1. Add a `Tab Name: { ... }` block in SECTION_LABELS.
 *   2. Author one `sectionKey -> "Human Title"` per visual row.
 *   3. If a visual row spans multiple section keys, add a merge entry
 *      in SECTION_MERGES that maps every raw key to a shared display
 *      key, then label that display key under SECTION_LABELS.
 */
export const SECTION_LABELS: Record<string, Record<string, string>> = {
  // MER-545: main Overview tab. One row per partner-tool KPI group, per
  // the approved Overview_Tab_Mockup.html.
  Overview: {
    "L0.1": "Disposal",
    "L0.2": "Containers · RO & FL",
    "L0.3": "Residential Carts",
    "L0.4": "Cash & Receivables",
    "L0.5": "Summary Tables",
  },
  // Wave 2: the Revenue tab is the rebuild of the client's revenue dashboard
  // (widgets L8r.*), one section per tab of that tool.
  Revenue: {
    "L8r.1": "Overview",
    "L8r.2": "By Month",
    "L8r.3": "By Division",
    "L8r.4": "By Line of Business",
    "L8r.5": "Roll Off",
    "L8r.6": "Front Load",
    "L8r.7": "Residential Accounts",
    "L8r.8": "Accounts",
    "L8r.9": "Credits and Adjustments",
    "L8r.10": "All Data",
    "L8r.11": "Fuel Surcharge",
  },
  // The previous Revenue tab, staff only since the wave 2 rebuild took its place.
  "Revenue Previous": {
    // Numeric ids (post-MER-178 sweep)
    "1.1": "Headline Revenue",
    "1.2": "Revenue by Operation",
    "1.3": "Per Operation P&L",
    "1.4": "Pricing",
    "1.5": "From Service to Cash",
    // L-prefix ids (MER-189: covers the bottom rows that flow into the
    // Revenue tab via auto-flow dense packing).
    // MER-623, Chris on the Delta review: "I don't understand this verb."
    // A waterfall is a chart type, not a thing a hauler tracks. The section
    // is named for what its four tiles actually walk through: work performed,
    // then invoiced, then paid.
    "L1.0": "From Service to Cash",
    "L1.1b": "Cash & Collections",
    "L1.3": "Revenue Trend",
    "L1.4": "Unit Economics",
    "L1.5": "Deferred Revenue",
  },

  // MER-199: Billing & Collections.
  "Billing & Collections": {
    "billing-headline": "Headline KPIs",
    "L1b.1": "AR Aging",
    "billing-payment-health": "Payment Health",
    "L1b.6": "Cash Collections",
    "billing-ar-risk": "AR Risk Signals",
    "billing-timing": "Billing Timing",
    "L2d.1": "Unbilled Work",
  },

  // MER-200: Operations.
  // 2.1.1 / L2b.0 / L2c.0 header_strip widgets already render section
  // chrome on this tab; FL/RO/ASL weekly KPI groups deliberately stay
  // unlabeled below them so the chrome isn't double-stamped.
  Operations: {
    "1.1": "Disposal Headline",
    "1.4": "Disposal Trend",
    "L2a.3": "Front Load Quality",
    "ops-fl-routes": "Front Load Routes",
    "L2b.2": "Roll Off Trends",
    "L2b.3": "Roll Off Container Idle",
    // MER-837: L2b.4 and L2b.5 are one authored row (span 6 + span 6 on
    // grid.row 11), merged into one group below so they render side by side
    // under one header instead of as two rows at half width each.
    "ops-ro-drivers-routes": "Roll Off Drivers and Routes",
    "L2b.6": "Roll Off Schedule Adherence",
    "L2c.2": "Side Load Completion",
    "ops-asl-quality": "Side Load Quality",
    "L2d.0": "Fleet Status",
    "ops-fleet-volume": "Fleet Volume",
    "ops-productivity": "Productivity",
    "ops-exceptions": "Exceptions",
    "ops-strategic": "Strategic Metrics",
  },

  // MER-201: Cost & Disposal.
  "Cost & Disposal": {
    "cost-disposal-economics": "Disposal Economics",
    "L3a.2": "Tonnage Mix",
    "L3a.3": "Unit Costs",
    "cost-labor-maint": "Labor & Maintenance",
    "cost-fuel-by-lob": "Fuel Cost by LOB",
    "cost-rankings": "Rankings",
    "cost-revenue-burden": "Revenue Burden",
  },

  // MER-202: Safety.
  // MER-579: was `Safety`, but every template and both resolved manifests
  // use tab id "Safety & Fleet" (renamed in MER-361 / #343, which updated the
  // Sales / Executive / Internal DQ keys and missed this one). The lookup
  // missed entirely, so this tab rendered ZERO section headers and its merge
  // rules never fired: 8 widgets scattered into 6 unlabelled groups.
  "Safety & Fleet": {
    "safety-headline": "Fleet Safety Headline",
    "L3b.2": "Safety by Division",
    "L3b.3": "Vehicle Rankings",
    "L3b.4": "Event Detail",
  },

  // MER-203: Maintenance (Fleetio pending).
  Maintenance: {
    "maint-kpis": "Maintenance KPIs (Fleetio pending)",
  },

  // MER-204: Profitability.
  Profitability: {
    "L4.1": "Contribution Margin",
    "prof-margin-by-lob": "Margin by LOB",
    "L4.2": "Route Margins",
    "L4.2b": "Route Ranking",
    "prof-route-flags": "Route Margin Flags",
    "prof-margin-detail": "Margin Detail",
    "prof-customer-margin": "Customer Margin",
  },

  // MER-205: Benchmarking.
  Benchmarking: {
    "bench-regional": "Regional Benchmarks",
  },

  // MER-206: Financial Health.
  "Financial Health": {
    "fh-snapshot": "Executive Health Snapshot",
    "L6a.2": "Top Outstanding Accounts",
  },

  // MER-207: Sales Pipeline. MER-361: renamed from Sales.
  "Sales Pipeline": {
    "sales-emr-headline": "EMR Headline",
    "L6b.2": "Contract Expiration Risk",
    "sales-risk": "Risk Indicators",
    "L6b.5": "Upcoming Expirations",
    "sales-ops": "Sales Operations",
    "L6b.6d": "Sales Rep Detail",
  },

  // MER-209: Data Quality (Meridian-internal tab; terser copy ok).
  // MER-361: renamed from Internal DQ to match customer-facing URL slug.
  "Data Quality": {
    "dq-mapping-gaps": "Mapping Gaps",
    "dq-freshness": "Data Freshness",
    "dq-coverage": "Coverage Metrics",
    "dq-audit": "Audit Findings",
  },

  // MER-210: Executive Roll-Up. MER-361: renamed from Executive.
  "Executive Roll-Up": {
    "exec-headline": "Executive Headline",
    "L6c.5": "Growth Trend",
  },

  // MER-717 / MER-705: the Meridian Board (staff only until AJ switches it on).
  // One section per board catalogue group; L7.8 holds the metrics that wait on
  // a client feed Meridian does not ingest yet.
  // Wave 1 (AJ 2026-09-24): the Disposal tab, Zay's Disposal Tracker.
  Disposal: {
    "L8d.1": "Disposal Summary",
    "L8d.2": "Yard Basis and Material Mix",
    "L8d.3": "By Division and Facility",
    "L8d.4": "Drivers and Customer Sites",
    "L8d.5": "Operational Review",
    "L8d.6": "Daily and Monthly",
    "L8d.7": "Ticket Detail",
    "L8d.8": "Manual Rates",
  },
  Board: {
    "L7.1": "Revenue",
    "L7.2": "Disposal Cost and Tonnage",
    "L7.3": "Service Units Completed",
    "L7.4": "Revenue per Service Unit",
    "L7.5": "Service Reliability",
    "L7.6": "Active Customers",
    "L7.7": "Receivables and Contracts",
    "L7.8": "Awaiting Client Feeds",
  },

  // Wave 1 rebuild on the client's AR aging dashboard (staff only until AJ
  // reviews it). One section per panel group of that tool.
  "AR Aging": {
    "L1c.1": "Aging Buckets",
    "L1c.2": "Commercial and Residential",
    "L1c.3": "Distribution and Account Status",
    "L1c.4": "Collections Priority",
    "L1c.5": "Critical Accounts",
    "L1c.6": "By Bucket",
    "L1c.7": "All Accounts",
    "L1c.8": "Awaiting Staff Input",
  },

  // Containers tab (wave 1, Delta's Container Inventory tool, staff only).
  Containers: {
    "L8c.1": "Container Activity",
    "L8c.2": "Inventory Snapshot",
    "L8c.3": "Monthly Summary",
    "L8c.4": "Analytics",
    "L8c.5": "Monthly Memo",
    "L8c.6": "Work Order Detail",
    "L8c.7": "Physical Counts and Reconciliation",
  },
};

/**
 * Per-tab merge map: raw `widgetSection()` key -> display group key.
 *
 * Used when multiple raw section keys belong to the same visual row,
 * e.g. Sales tab "EMR Headline" pulls in L6b.1a and L6b.1b which
 * resolve to "L6b.1" individually (a/b strip), so the merge collapses
 * that single key under "sales-emr-headline". Mixed-prefix rows
 * (numeric + L-prefix) live here too, e.g. Billing & Collections
 * "Headline KPIs" merges "1.1" (1.1.5 DSO) with "L1b.11" into
 * "billing-headline".
 *
 * Keys with no merge entry pass through unchanged to SECTION_LABELS.
 */
export const SECTION_MERGES: Record<string, Record<string, string>> = {
  "Billing & Collections": {
    // Top tile row: 1.1.5 (DSO) + L1b.11 (Billed Revenue).
    "1.1": "billing-headline",
    "L1b.11": "billing-headline",
    // Payment + collection trio.
    "L1b.2": "billing-payment-health",
    "L1b.4": "billing-payment-health",
    "L1b.5": "billing-payment-health",
    // Cash health trio.
    "L1b.7": "billing-ar-risk",
    "L1b.8": "billing-ar-risk",
    "L1b.9": "billing-ar-risk",
    // Cash flow timing: 1.5.3. L1b.10 was a byte-identical second copy of it
    // on this same tab and was deleted (MER-638); the merge key stays because
    // the row still exists.
    "1.5": "billing-timing",
  },

  Operations: {
    // FL trends: L2a.2, L2a.4, L2a.5 -> one "Front Load Routes" row.
    "L2a.2": "ops-fl-routes",
    "L2a.4": "ops-fl-routes",
    "L2a.5": "ops-fl-routes",
    // MER-837: the two Roll Off ranking tables. Both author grid.row 11 at
    // span 6, so together they are one full-width row. They carried different
    // section prefixes, and planSectionRows runs once per SECTION, so the
    // grouper split the authored row and each table rendered alone at 6 of 12
    // tracks under its own header. Nothing was dropped, which is what makes
    // this distinct from MER-609. One merge key puts them back on one line.
    "L2b.4": "ops-ro-drivers-routes",
    "L2b.5": "ops-ro-drivers-routes",
    // ASL quality: L2c.3a / L2c.3b.
    "L2c.3": "ops-asl-quality",
    // Cross LOB exceptions, productivity, etc.
    "L2d.1": "ops-fleet-volume",
    "L2d.2": "ops-fleet-volume",
    "L2d.3": "ops-productivity",
    "L2d.4": "ops-exceptions",
    "L2d.5": "ops-strategic",
  },

  "Cost & Disposal": {
    // L3a.1a/b headline.
    "L3a.1": "cost-disposal-economics",
    // L3a.4a (Maint Cost / Mile) + L3a.4-labor.
    "L3a.4": "cost-labor-maint",
    // Per-LOB fuel cards all live under L3a.4b (fl/ro/asl share key
    // L3a.4 after letter strip, see cost-labor-maint). Move them to
    // their own group by manifest authoring; in the meantime the
    // L3a.4 merge above absorbs them.
    // L3a.4c + L3a.4e rankings.
    "L3a.4c": "cost-rankings",
    "L3a.4e": "cost-rankings",
    // L3a.4f (Fuel as % Revenue) + L3a.4g (Maint as % Revenue).
    "L3a.4f": "cost-revenue-burden",
    "L3a.4g": "cost-revenue-burden",
  },

  "Safety & Fleet": {
    // 5 widgets across "3.1", "4.1", "L3b.1" merge into one headline.
    "3.1": "safety-headline",
    "4.1": "safety-headline",
    "L3b.1": "safety-headline",
  },

  Maintenance: {
    // L3c.1 + L3c.2 placeholder KPIs.
    "L3c.1": "maint-kpis",
    "L3c.2": "maint-kpis",
  },

  Profitability: {
    // L4.1b-fl/ro/asl per-LOB margin (all share key L4.1 after strip,
    // collide with the L4.1 Contribution Margin After Disposal tile). The merge
    // disambiguates by hoisting L4.1b cards into their own group;
    // L4.1 (no trailing letter) stays as Contribution Margin.
    "L4.1b": "prof-margin-by-lob",
    // Route flag count trio.
    "L4.3": "prof-route-flags",
    // L4.4a + L4.4b margin detail.
    "L4.4": "prof-margin-detail",
    // L4.5 + L4.6 + L4.7 customer margin.
    "L4.5": "prof-customer-margin",
    "L4.6": "prof-customer-margin",
    "L4.7": "prof-customer-margin",
  },

  Benchmarking: {
    "L5.1": "bench-regional",
    "L5.2": "bench-regional",
  },

  "Financial Health": {
    // L6a.1a/b/c snapshot tiles.
    "L6a.1": "fh-snapshot",
  },

  "Sales Pipeline": {
    // L6b.1a + L6b.1b EMR headline.
    "L6b.1": "sales-emr-headline",
    // L6b.3a/b/c risk indicators.
    "L6b.3": "sales-risk",
    // L6b.6a (Portfolio) + L6b.6b (MRR) + L6b.6c (Billing Mix)
    // = ops row. L6b.6d (rep ranking) gets its own section.
    "L6b.6a": "sales-ops",
    "L6b.6b": "sales-ops",
    "L6b.6c": "sales-ops",
  },

  "Data Quality": {
    // L3d.1a/b/c mapping gaps.
    "L3d.1": "dq-mapping-gaps",
    // L3d.2a/b/c freshness.
    "L3d.2": "dq-freshness",
    // L3d.3a/b coverage.
    "L3d.3": "dq-coverage",
    // L3d.4a/b audit findings.
    "L3d.4": "dq-audit",
  },

  "Executive Roll-Up": {
    // L6c.1, L6c.2, L6c.3, L6c.4 all in the headline row.
    "L6c.1": "exec-headline",
    "L6c.2": "exec-headline",
    "L6c.3": "exec-headline",
    "L6c.4": "exec-headline",
  },
};

/**
 * Apply the per-tab merge map: returns the display group key for a
 * raw `widgetSection()` output, or the raw key when no merge exists.
 *
 * Called by `groupBySection()` in CustomerDashboard so widgets that
 * share a display group land in the same `<section>` block (and thus
 * share a CSS grid row / header).
 */
export function sectionGroupKey(tabId: string | undefined, sectionKey: string): string {
  if (!tabId) return sectionKey;
  const merge = SECTION_MERGES[tabId]?.[sectionKey];
  return merge ?? sectionKey;
}

/**
 * Resolve the section label for a rendered section group.
 *
 * Priority:
 *   1. Explicit entry in `SECTION_LABELS[tabId][groupKey]`.
 *   2. No entry: returns null so the renderer omits the header. The
 *      surrounding grid container still groups the widgets visually.
 */
export function sectionLabel(
  tabId: string | undefined,
  groupKey: string,
  widgets: ManifestWidget[],
): string | null {
  if (!tabId) return null;
  const fromMap = SECTION_LABELS[tabId]?.[groupKey];
  if (fromMap) return fromMap;
  if (widgets.length === 1) return null;
  return null;
}
